import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPaginationContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import { cutoffs } from '../../constants';

import PortfolioFolders from './PortfolioFolders';

import actions from '../../actions/FavoritesActions';
import get from 'lodash.get';

function FoldersPaginationWrapper(props) {
    const totalResults = get(props, 'viewer.portfolios.totalResults');

    const {
        setPartialPageLoading,
        setTotalResults,
        relay: { hasMore, loadMore, isLoading },
        increaseNumItems,
    } = props;

    useEffect(() => {
        function loadMoreItems() {
            if (hasMore()) {
                setPartialPageLoading(true);
                loadMore(cutoffs.ADDITIONAL_ITEMS, () => {
                    setPartialPageLoading(false);
                    increaseNumItems(cutoffs.ADDITIONAL_ITEMS);
                });
            }
        }

        function handleScroll() {
            const positionY = window.scrollY || window.pageYOffset;
            window.requestAnimationFrame(() => {
                const scrollPosition = window.innerHeight + positionY;
                const halfDocumentHeight = document.body.offsetHeight * 0.5;

                const threshold = scrollPosition >= halfDocumentHeight;

                if (threshold && hasMore() && !isLoading()) {
                    loadMoreItems();
                }
            });
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            setPartialPageLoading(false);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasMore, isLoading, loadMore, setPartialPageLoading, increaseNumItems]);

    useEffect(() => {
        if (totalResults >= 0) {
            setTotalResults(totalResults);
        }
    }, [totalResults, setTotalResults]);

    if (!totalResults) {
        return null;
    }

    return (
        <PortfolioFolders
            onDrop={props.onDrop}
            selectedItems={props.selectedItems}
            portfoliosConnection={props.viewer.portfolios || null}
            reordered={props.reordered}
        />
    );
}

FoldersPaginationWrapper.propTypes = {
    first: PropTypes.number,
    onDrop: PropTypes.func,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    selectedItems: PropTypes.array,
    increaseNumItems: PropTypes.func,
    setPartialPageLoading: PropTypes.func,
    setTotalResults: PropTypes.func,
    sortBy: PropTypes.string,
    viewer: PropTypes.object,
    page: PropTypes.number,
};

const mapStateToProps = state => {
    return {
        totalResults: state.favorites.uiState.totalResults,
        page: state.favorites.uiState.page,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        increaseNumItems: first => {
            dispatch(actions.increaseNumItems({ first }));
        },
        setPartialPageLoading: isLoadingPartialPage => {
            dispatch(actions.setPartialPageLoading({ isLoadingPartialPage }));
        },
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(FoldersPaginationWrapper);

FoldersPaginationWrapper = createPaginationContainer(
    ReduxWrapped,
    {
        viewer: graphql`
            fragment FoldersPaginationWrapper_viewer on Viewer {
                portfolios(
                    first: $first
                    after: $after
                    portfolioTypes: $portfolioTypes
                    sortBy: $foldersSort
                    userIds: $userIds
                ) @connection(key: "FoldersPaginationWrapper_portfolios") @include(if: $hasUserId) {
                    totalResults
                    numberOfPages
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    edges {
                        cursor
                    }
                    ...PortfolioFolders_portfoliosConnection
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.viewer && props.viewer.portfolios;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }, fragmentVariables) {
            return {
                first: count,
                after: cursor,
                foldersSort: props.sortBy,
                portfolioTypes: fragmentVariables.portfolioTypes,
                userIds: fragmentVariables.userIds,
                hasUserId: fragmentVariables.hasUserId,
            };
        },
        query: graphql`
            query FoldersPaginationWrapperRefetchQuery(
                $after: String
                $first: Int!
                $foldersSort: PortfoliosSortByTypes
                $portfolioTypes: [PortfolioTypes]
                $userIds: [String]!
                $hasUserId: Boolean!
            ) {
                viewer {
                    ...FoldersPaginationWrapper_viewer
                }
            }
        `,
    }
);

export default FoldersPaginationWrapper;
